import React from 'react';
import { View, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { IconStatCard } from './IconStatCard';
import { SectionHeader } from '../SectionHeader';

interface QuickActionsSectionProps {
  openTicketsCount: number;
  /** Newsfeed threads posted since the resident last opened the board. */
  newsfeedNewCount: number;
  unreadMessagesCount: number;
  /** Open surveys the resident hasn't responded to yet. */
  surveysCount: number;
  onMaintenance: () => void;
  onNewsfeed: () => void;
  onMessages: () => void;
  onSurveys: () => void;
  /** Override individual item wrapper style (e.g. fixed width on mobile) */
  itemStyle?: StyleProp<ViewStyle>;
  style?: StyleProp<ViewStyle>;
}

export const QuickActionsSection: React.FC<QuickActionsSectionProps> = React.memo(({
  openTicketsCount,
  newsfeedNewCount,
  unreadMessagesCount,
  surveysCount,
  onMaintenance,
  onNewsfeed,
  onMessages,
  onSurveys,
  itemStyle,
  style,
}) => {
  const { colors } = useThemeColors();

  const items = [
    {
      key: 'maintenance',
      label: 'Maintenance',
      caption: `${openTicketsCount} open`,
      iconBg: colors.accent.orange,
      Icon: RESIDENTS_SVG_COMPONENTS.WRENCH,
      iconColor: PRIMITIVE_COLORS.warningOrange,
      onPress: onMaintenance,
    },
    {
      key: 'newsfeed',
      label: 'Newsfeed',
      caption: `${newsfeedNewCount} new`,
      iconBg: colors.accent.purple,
      Icon: RESIDENTS_SVG_COMPONENTS.BOARD,
      iconColor: PRIMITIVE_COLORS.resioPurple,
      onPress: onNewsfeed,
    },
    {
      key: 'messages',
      label: 'Messages',
      caption: `${unreadMessagesCount} unread`,
      iconBg: colors.accent.blueBg,
      Icon: RESIDENTS_SVG_COMPONENTS.CHAT_ICON,
      iconColor: PRIMITIVE_COLORS.resioBlue,
      onPress: onMessages,
    },
    {
      key: 'surveys',
      label: 'Surveys',
      caption: `${surveysCount} to answer`,
      iconBg: colors.accent.greenLight,
      Icon: RESIDENTS_SVG_COMPONENTS.QUESTION_BUBBLE,
      iconColor: PRIMITIVE_COLORS.successGreen,
      onPress: onSurveys,
    },
  ];

  return (
    <View style={style}>
      <SectionHeader title="Quick Actions" />
      <View style={styles.grid}>
        {items.map((item) => (
          <IconStatCard
            key={item.key}
            label={item.label}
            caption={item.caption}
            iconBg={item.iconBg}
            Icon={item.Icon}
            iconColor={item.iconColor}
            onPress={item.onPress}
            itemStyle={itemStyle}
            showCountBadge
          />
        ))}
      </View>
    </View>
  );
});

QuickActionsSection.displayName = 'QuickActionsSection';

const styles = StyleSheet.create({
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: REDESIGN_SPACING.gridGap,
  },
});
